import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Select, { components } from 'react-select';
import PropTypes from 'prop-types';

import RealEstateAddressOption from '@/orderForm/fields/RealEstateAdressesSelectField/RealEstateAddressOption';

const RealEstateAdressesSelectField = ({ formData, onChange, uiSchema }) => {
  const intl = useIntl();

  const realEstateOptions = uiSchema['ui:options'] || [];
  const label = uiSchema['ui:label'];
  const id = uiSchema['ui:id'];

  const mappedAdressesOptions = realEstateOptions?.map(({ public_id, name, address }) => {
    const streetText = address?.street ? `${address?.street}, ` : '';
    const numberText = address?.number ? `${address?.number}, ` : '';
    const neighborhoodText = address?.neighborhood ? `${address?.neighborhood}, ` : '';
    const cityText = address?.city || '';

    return {
      label: name,
      value: public_id,
      realEstateAddressText: streetText + numberText + neighborhoodText + cityText,
    };
  });

  const isSelectBlockedWithOneOption = !realEstateOptions?.length || realEstateOptions?.length <= 1;

  if (isSelectBlockedWithOneOption) return null;

  return (
    <div className="flex flex-col gap-2 mb-6 animate-in fade-in slide-in-from-top-2 duration-300">
      {label && (
        <label className="text-[12px] font-bold text-gray-500 uppercase tracking-tight ml-1">
          <FormattedMessage id={label} />
        </label>
      )}

      <Select
        id={id}
        options={mappedAdressesOptions || []}
        onChange={option => onChange(option?.value)}
        value={mappedAdressesOptions?.find(option => option?.value === formData) || null}
        components={{
          Input: inputProps => <components.Input {...inputProps} data-cy={id} />,
          Option: RealEstateAddressOption,
        }}
        placeholder={intl.formatMessage({ id: 'SELECT_A_REAL_ESTATE' })}
        noOptionsMessage={() => intl.formatMessage({ id: 'NO_OPTIONS_AVAILABLE_MESSAGE' })}
        isDisabled={isSelectBlockedWithOneOption}
        className="w-full"
        styles={{
          control: (base, state) => ({
            ...base,
            backgroundColor: 'white',
            borderColor: state.isFocused ? '#5882C1' : '#E5E7EB',
            borderRadius: '0.75rem',
            padding: '2px 4px',
            fontSize: '14px',
            boxShadow: state.isFocused ? '0 0 0 2px rgba(88, 130, 193, 0.2)' : 'none',
            '&:hover': {
              borderColor: '#5882C1',
            },
            transition: 'all 0.2s ease',
          }),
          menu: base => ({
            ...base,
            borderRadius: '0.75rem',
            overflow: 'hidden',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            border: '1px border-gray-100',
            marginTop: '8px',
            zIndex: 50,
          }),
          option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected ? '#5882C1' : state.isFocused ? '#5882C1/10' : 'transparent',
            color: state.isSelected ? 'white' : '#374151',
            '&:active': {
              backgroundColor: '#5882C1',
            },
          }),
          placeholder: base => ({
            ...base,
            color: '#9CA3AF',
          }),
          singleValue: base => ({
            ...base,
            color: '#374151',
            fontWeight: '500',
          }),
        }}
      />
    </div>
  );
};

RealEstateAdressesSelectField.propTypes = {
  formData: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  uiSchema: PropTypes.object,
};

RealEstateAdressesSelectField.defaultProps = {
  formData: null,
  uiSchema: null,
};

export default RealEstateAdressesSelectField;
